import { useLayoutEffect, useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger, cn } from "@nexus/ui";

const ESTIMATED_CHARS_PER_LINE = 36;

interface ClampedDescriptionProps {
  text: string;
  className?: string;
  /** Matches the line-clamp used in className; enables tooltip when measure misses. */
  maxLines?: 1 | 2 | 3;
}

export function ClampedDescription({ text, className, maxLines = 3 }: ClampedDescriptionProps) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      setIsTruncated(el.scrollHeight - el.clientHeight > 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, className]);

  const showTooltip = isTruncated || text.length > maxLines * ESTIMATED_CHARS_PER_LINE;

  const clampClass = maxLines === 1 ? 'line-clamp-1' : maxLines === 2 ? 'line-clamp-2' : 'line-clamp-3';

  const paragraph = (
    <p
      ref={ref}
      className={cn(
        clampClass,
        "min-w-0 w-full break-words text-[11px] leading-snug text-muted-foreground/75",
        showTooltip && "cursor-default",
        className,
      )}
    >
      {text}
    </p>
  );

  if (!showTooltip) return paragraph;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{paragraph}</TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        className="max-h-48 max-w-sm overflow-y-auto whitespace-pre-wrap break-words"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
